import {
  ChatCitations,
  ConfidenceShade,
  AbstentionNotice,
  Markdown,
  type CitationData,
} from "@aisquare/ui";
import { Section, VariantBlock } from "../lib/Section";

// Plain citations (F5): numbered markers + collapsible panel. No F6 signals,
// so confidence shading / freshness badges stay dormant.
const plain: CitationData[] = [
  {
    source: {
      title: "Recharts - composable charting built on React + D3",
      url: "https://www.recharts.org/",
      snippet: "A redefined chart library built with React and D3.",
    },
  },
  {
    source: {
      title: "K2 block schema registry",
      url: "https://docs.aisquare.studio/contracts/block-registry",
      snippet: "Blocks are how the LLM puts rich UI in chat.",
    },
  },
];

// Lit-up F6: per-source support + freshness, one weak source.
const now = Date.now();
const graded: CitationData[] = [
  {
    claim_ref: "c1",
    source: {
      title: "Primary source (well grounded)",
      url: "https://arxiv.org/abs/2401.00001",
      snippet: "Peer-reviewed result supporting the claim directly.",
      freshness: now - 3 * 24 * 60 * 60 * 1000,
    },
    support: 0.92,
  },
  {
    claim_ref: "c1",
    source: {
      title: "Secondary source (partial)",
      url: "https://nature.com/articles/x",
      snippet: "Related but indirect supporting evidence.",
      freshness: now - 40 * 24 * 60 * 60 * 1000,
    },
    support: 0.55,
  },
  {
    claim_ref: "c2",
    source: {
      title: "Weak source",
      url: "https://example.com/blog/opinion",
      snippet: "Opinion piece, weak grounding.",
      freshness: now - 400 * 24 * 60 * 60 * 1000,
    },
    support: 0.2,
  },
];

export function ChatCitationsDemo() {
  return (
    <Section
      id="chat-citations"
      title="ChatCitations / SourcesPanel"
      description="Renders the streamed `data-citation` parts: inline numbered markers wired to a collapsible sources panel, hover/focus preview, per-claim confidence shading, freshness badges, and an abstention state. The support / freshness / abstained signals are dormant until the runtime citation gate emits them."
    >
      <VariantBlock label="Plain (F5)" caption="numbered markers + panel; F6 signals dormant">
        <div className="w-full max-w-xl">
          <Markdown content="The platform streams rich UI as typed blocks, and cites every claim back to its sources." />
          <ChatCitations citations={plain} showMarkers />
        </div>
      </VariantBlock>

      <VariantBlock
        label="Graded (F6 lit up)"
        caption="per-source support + relative-time freshness; click a marker to open + highlight"
      >
        <div className="w-full max-w-xl">
          <Markdown content="Two sources back the first claim; one weak source backs the second." />
          <ChatCitations citations={graded} showMarkers defaultOpen />
        </div>
      </VariantBlock>

      <VariantBlock label="Confidence shading" caption="per-claim, de-emphasizes weak grounding">
        <div className="w-full max-w-xl space-y-3">
          <ConfidenceShade support={0.92}>
            <Markdown content="**Well grounded** claim renders at full emphasis." />
          </ConfidenceShade>
          <ConfidenceShade support={0.55}>
            <Markdown content="**Partial** claim is slightly dimmed." />
          </ConfidenceShade>
          <ConfidenceShade support={0.2}>
            <Markdown content="**Weak** claim is dimmed further so the eye discounts it." />
          </ConfidenceShade>
        </div>
      </VariantBlock>

      <VariantBlock label="Abstention" caption='the explicit "I can&apos;t ground this" state'>
        <div className="w-full max-w-xl space-y-3">
          <AbstentionNotice abstained scope="this" />
          <AbstentionNotice abstained scope="claim" />
          <ChatCitations citations={[]} abstained />
        </div>
      </VariantBlock>
    </Section>
  );
}
